import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CONTACT } from '../../lib/contact';

// Server innobek.uz → www.innobek.uz (308) yo'naltiradi, shuning uchun
// kanonik manzil — www bilan. Sitemap va hreflang ham shu bilan mos.
export const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.innobek.uz';

/**
 * Sahifa meta teglari + JSON-LD.
 *
 * Eslatma: sayt hozir CSR (CRA). Google JS'ni ijro etadi, lekin
 * ishonchli indekslash uchun build bosqichida prerender kerak —
 * qarang: seo-reports/audit-infra.md
 */
function Seo({
    title,
    description,
    keywords,
    lang = 'uz',
    path = '/',
    altPath,
    image = '/assets/img/og-default.png',
    type = 'website',
    schema,
    noindex = false,
}) {
    const url = `${SITE_URL}${path}`;
    const ogImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

    return (
        <Helmet prioritizeSeoTags>
            <html lang={lang} />
            <title>{title}</title>
            <meta name="description" content={description} />
            {keywords?.length ? <meta name="keywords" content={keywords.join(', ')} /> : null}
            <link rel="canonical" href={url} />
            {noindex ? <meta name="robots" content="noindex, nofollow" /> : (
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
            )}

            {/* hreflang — uz ↔ ru juftligi */}
            {altPath ? (
                <link rel="alternate" hrefLang={lang === 'uz' ? 'ru' : 'uz'} href={`${SITE_URL}${altPath}`} />
            ) : null}
            <link rel="alternate" hrefLang={lang} href={url} />
            <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${lang === 'ru' && altPath ? altPath : path}`} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Innobek" />
            <meta property="og:locale" content={lang === 'ru' ? 'ru_RU' : 'uz_UZ'} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {schema ? (
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            ) : null}
        </Helmet>
    );
}

/** Butun sayt uchun tashkilot sxemasi — App darajasida bir marta. */
export function organizationSchema(lang = 'uz') {
    return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        '@id': `${SITE_URL}/#organization`,
        name: 'Innobek',
        alternateName: 'Innobek IT',
        url: SITE_URL,
        logo: `${SITE_URL}/assets/img/logo.png`,
        image: `${SITE_URL}/assets/img/og-default.png`,
        telephone: CONTACT.phoneDisplay,
        priceRange: '$$',
        address: {
            '@type': 'PostalAddress',
            streetAddress: "U-ENTER innovatsion markazi, Shahrisabz ko'chasi",
            addressLocality: 'Toshkent',
            postalCode: '700093',
            addressCountry: 'UZ',
        },
        openingHoursSpecification: [{
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '09:00',
            closes: '21:00',
        }],
        sameAs: [CONTACT.telegram, CONTACT.linkedin].filter(Boolean),
        areaServed: { '@type': 'Country', name: 'Uzbekistan' },
        inLanguage: lang,
    };
}

/** Xizmat sahifasi uchun Service + BreadcrumbList + FAQPage */
export function serviceSchema({ content, path, lang, servicesLabel, servicesPath }) {
    const graph = [
        {
            '@type': 'Service',
            '@id': `${SITE_URL}${path}#service`,
            name: content.hero.h1,
            description: content.meta.description,
            serviceType: content.hero.eyebrow,
            provider: { '@id': `${SITE_URL}/#organization` },
            areaServed: { '@type': 'Country', name: 'Uzbekistan' },
            url: `${SITE_URL}${path}`,
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Innobek', item: SITE_URL },
                { '@type': 'ListItem', position: 2, name: servicesLabel, item: `${SITE_URL}${servicesPath}` },
                { '@type': 'ListItem', position: 3, name: content.hero.h1, item: `${SITE_URL}${path}` },
            ],
        },
    ];

    if (content.faq?.items?.length) {
        graph.push({
            '@type': 'FAQPage',
            mainEntity: content.faq.items.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
        });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
}

export default Seo;
